import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CustomerList } from 'src/app/service/customer.model';
import {
  Category,
  CloseIssuePayload,
  FeedbackPath,
  FinanceInquiryPayload,
  IssueFeedBackData,
  IssueFeedBackDataResponse,
  MemberRole,
  OrganList,
  Params,
  ReassignReviewPayload,
  ReopenIssuePayload,
  ServiceRequestErrorPayload,
  ServiceRequestInfo,
  TopsErrorFormData,
  TypesAndCategoriesResponse,
  UpdateReviewPayload,
} from '../models/issue-feedback.interface';

@Injectable({
  providedIn: 'root',
})
export class IssuesFeedbackService {
  private readonly httpClient = inject(HttpClient);

  private readonly serviceRequestIssueFeedbackDataSubject = new BehaviorSubject<ServiceRequestInfo | null>(null);
  readonly serviceRequestIssueFeedbackData$ = this.serviceRequestIssueFeedbackDataSubject.asObservable();

  private readonly issueDetailsDataSubject = new BehaviorSubject<IssueFeedBackData | null>(null);
  readonly issueDetailsData$ = this.issueDetailsDataSubject.asObservable();

  updateServiceRequestIssueFeedbackData(data: ServiceRequestInfo): void {
    this.serviceRequestIssueFeedbackDataSubject.next(data);
  }

  clearServiceRequestIssueFeedbackData(): void {
    this.serviceRequestIssueFeedbackDataSubject.next(null);
  }

  getAssignedFeedbacks(params: Params): Observable<IssueFeedBackDataResponse> {
    const url = `${environment.apiHostV2}/feedbacks/assignedToMe`;
    return this.httpClient.post<IssueFeedBackDataResponse>(url, params);
  }

  getAssignedCountFeedbacks(): Observable<number> {
    const url = `${environment.apiHostV2}/feedbacks/countAssignedToMe`;
    return this.httpClient.get<number>(url);
  }

  getOpenFeedbacks(params: Params): Observable<IssueFeedBackDataResponse> {
    const url = `${environment.apiHostV2}/feedbacks/getOpen`;
    return this.httpClient.post<IssueFeedBackDataResponse>(url, params);
  }

  getAllIssuesFeedbacks(params: Params): Observable<IssueFeedBackDataResponse> {
    const url = `${environment.apiHostV2}/feedbacks/search`;
    return this.httpClient.post<IssueFeedBackDataResponse>(url, params);
  }

  getTripNumbersForCustomer(customerId: string): Observable<{ id: string; name: string }[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripNumbersForCustomer/${customerId}`;
    return this.httpClient.get<{ id: string; name: string }[]>(url);
  }

  getCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/feedbacks/categories`;
    return this.httpClient.get<Category[]>(url);
  }

  getTOPSCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/Tops/categories`;
    return this.httpClient.get<Category[]>(url);
  }

  getFinanceCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/feedbacks/financeCategories`;
    return this.httpClient.get<Category[]>(url);
  }

  getOrganList(): Observable<OrganList[]> {
    const url = `${environment.apiHostV2}/Tops/organs`;
    return this.httpClient.get<OrganList[]>(url);
  }

  getEmployeeList(): Observable<{ id: string; name: string }[]> {
    const url = `${environment.apiHost}/Member/InternalMembers`;
    return this.httpClient.get<{ id: string; name: string }[]>(url);
  }

  getCustomerList(): Observable<CustomerList[]> {
    const url = `${environment.apiHost}/Customer/GetAll`;
    return this.httpClient.get<CustomerList[]>(url);
  }

  getTypesAndCategories(): Observable<TypesAndCategoriesResponse[]> {
    const url = `${environment.apiHostV2}/feedbacks/typesAndCategories`;
    return this.httpClient.get<TypesAndCategoriesResponse[]>(url);
  }

  getTripIDList(tripId?: string): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripsBasedOnTripNumber${tripId ? `/${tripId}` : ''}`;
    return this.httpClient.get<string[]>(url);
  }

  createServiceRequestIssueFeedback(data: ServiceRequestErrorPayload | FinanceInquiryPayload, path: FeedbackPath): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/${path}`;
    return this.httpClient.post<void>(url, data);
  }

  updateServiceRequestIssueFeedback(data: ServiceRequestErrorPayload | FinanceInquiryPayload): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/editIssue`;
    return this.httpClient.put<void>(url, data);
  }

  getFeedbackDetails(id: string): Observable<IssueFeedBackData> {
    const url = `${environment.apiHostV2}/feedbacks/${id}`;
    return this.httpClient.get<IssueFeedBackData>(url).pipe(tap((res) => this.issueDetailsDataSubject.next(res)));
  }

  clearIssueDetailsData(): void {
    this.issueDetailsDataSubject.next(null);
  }

  updateReviewFeedback(payload: UpdateReviewPayload): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/reviewFeedback`;
    return this.httpClient.post<void>(url, payload);
  }

  reopenIssue(payload: ReopenIssuePayload): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/reopenIssue`;
    return this.httpClient.put<void>(url, payload);
  }

  closeIssue(payload: CloseIssuePayload): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/closeIssue`;
    return this.httpClient.post<void>(url, payload);
  }

  reassignReview(payload: ReassignReviewPayload): Observable<void> {
    const url = `${environment.apiHostV2}/feedbacks/reassignFeedback`;
    return this.httpClient.post<void>(url, payload);
  }

  createTopsErrorFeedback(data: TopsErrorFormData): Observable<string> {
    const url = `${environment.apiHostV2}/Tops`;
    return this.httpClient.post<string>(url, data);
  }

  getCoordinatorList(): Observable<MemberRole[]> {
    const url = `${environment.apiHost}/MemberRole/fetchInternalCoordinator`;
    return this.httpClient.get<MemberRole[]>(url);
  }

  getSupervisorList(): Observable<MemberRole[]> {
    const url = `${environment.apiHost}/MemberRole/fetchManagers`;
    return this.httpClient.get<MemberRole[]>(url);
  }
}
